/**
 * Contrato del panel de filtros: dominio conceptual + tipo de componente UI.
 * El motor (tile / DB) se resuelve en parcelFilterMatch según el modo de cada filtro.
 */

import { OPERATIONS, CURRENCIES } from '../utils/listingCoreConstants';
import {
  POBREZA_CRONICA_ARGENTINA_BUCKETS,
  ZONAS_INUNDABLES_LEGEND,
} from '../components/LayerSelectorPanel/MapLayerOptions';

/**
 * `matchValues` cubre las variantes con las que el catastro escribe los tipos
 * compuestos ("SUBURBANO", "SUB URBANO", "SUB-URBANO"): sin ellas el filtro
 * dejaría el mapa vacío en las provincias que usan otra grafía.
 */
const TYPE_OPTIONS = [
  { key: 'URBANO', value: 'URBANO', label: 'Urbano' },
  {
    key: 'SUBURBANO',
    value: 'SUBURBANO',
    label: 'Suburbano',
    matchValues: ['SUBURBANO', 'SUB URBANO', 'SUB-URBANO'],
  },
  { key: 'RURAL', value: 'RURAL', label: 'Rural' },
  {
    key: 'SUBRURAL',
    value: 'SUBRURAL',
    label: 'Subrural',
    matchValues: ['SUBRURAL', 'SUB RURAL', 'SUB-RURAL'],
  },
];

const STATE_OPTIONS = [
  { key: 'EDIFICADO PH', value: 'EDIFICADO PH', label: 'Edificado PH' },
  { key: 'EDIFICADO NoPH', value: 'EDIFICADO NoPH', label: 'Edificado NoPH' },
  { key: 'BALDIO', value: 'BALDIO', label: 'Baldío' },
];

const NEIGHBORHOOD_TYPE_OPTIONS = [
  { key: 'barrio_residencial', value: 'barrio_residencial', label: 'Barrio residencial' },
  { key: 'barrio_cerrado', value: 'barrio_cerrado', label: 'Barrio cerrado' },
  { key: 'barrio_popular', value: 'barrio_popular', label: 'Barrio popular' },
];

/** Quita acentos para comparar contra tiles que pueden no traerlos. */
const deburr = (text) => String(text).normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Opción de provincia: el valor es el nombre canónico y `matchValues` cubre las
 * variantes con y sin acento, porque no está garantizado que todos los tilesets
 * escriban `province_name` igual. El match ya ignora mayúsculas.
 */
const provinceOption = (name, aliases = []) => ({
  key: name,
  value: name,
  label: name,
  matchValues: Array.from(new Set([name, ...aliases].flatMap((n) => [n, deburr(n)]))),
});

const PROVINCE_OPTIONS = [
  provinceOption('Buenos Aires'),
  provinceOption('CABA', ['Ciudad Autónoma de Buenos Aires', 'Ciudad de Buenos Aires']),
  provinceOption('Catamarca'),
  provinceOption('Chaco'),
  provinceOption('Chubut'),
  provinceOption('Córdoba'),
  provinceOption('Corrientes'),
  provinceOption('Entre Ríos'),
  provinceOption('Formosa'),
  provinceOption('Jujuy'),
  provinceOption('La Pampa'),
  provinceOption('La Rioja'),
  provinceOption('Mendoza'),
  provinceOption('Misiones'),
  provinceOption('Neuquén'),
  provinceOption('Río Negro'),
  provinceOption('Salta'),
  provinceOption('San Juan'),
  provinceOption('San Luis'),
  provinceOption('Santa Cruz'),
  provinceOption('Santa Fe'),
  provinceOption('Santiago del Estero'),
  provinceOption('Tierra del Fuego', ['Tierra del Fuego, Antártida e Islas del Atlántico Sur']),
  provinceOption('Tucumán'),
];

/**
 * Nombre canónico de `province_name` / `view_province` (`Córdoba`, `CABA`, …).
 * @param {unknown} raw
 * @returns {string|null}
 */
export function canonicalizeProvinceName(raw) {
  const text = String(raw || '').trim();
  if (!text) return null;
  const needle = deburr(text).toLowerCase();
  const option = PROVINCE_OPTIONS.find((item) => (
    (item.matchValues || []).some((value) => deburr(String(value)).toLowerCase() === needle)
  ));
  return option ? option.value : null;
}

const ZONING_ZONE_OPTIONS = [
  { key: '8057-PERFIL X', value: '8057-PERFIL X', label: '8057 — Perfil X' },
  { key: '8256-G1', value: '8256-G1', label: '8256-G1' },
  { key: '8256-F3', value: '8256-F3', label: '8256-F3' },
  { key: '8256-H2', value: '8256-H2', label: '8256-H2' },
];

const ZONING_PERFIL_OPTIONS = [
  {
    key: 'perfil-x',
    value: 'cordoba_capital_ocupacion_suelo:8057-PERFIL X',
    label: 'Capital — Perfil X',
  },
];

const LOT_SHAPE_OPTIONS = [
  { key: 'rectangular', value: 'rectangular', label: 'Rectangular' },
  { key: 'rectangular_alargado', value: 'rectangular_alargado', label: 'Rectangular alargado' },
  { key: 'irregular', value: 'irregular', label: 'Irregular' },
  { key: 'triangular', value: 'triangular', label: 'Triangular' },
];

const STREET_POSITION_OPTIONS = [
  { key: 'midblock', value: 'midblock', label: 'Medio de cuadra' },
  { key: 'corner', value: 'corner', label: 'Esquina' },
  { key: 'island', value: 'island', label: 'Isla' },
];

const FRONT_HIGHWAY_OPTIONS = [
  { key: 'residential', value: 'residential', label: 'Residencial' },
  { key: 'primary', value: 'primary', label: 'Primaria' },
  { key: 'secondary', value: 'secondary', label: 'Secundaria' },
  { key: 'tertiary', value: 'tertiary', label: 'Terciaria' },
  { key: 'motorway', value: 'motorway', label: 'Autopista' },
];

/**
 * Bandas sobre campo bucketizado del tile (`bld_b`): match exacto.
 * `min`/`max` son el equivalente en valor crudo, para evaluar la misma banda
 * contra `filter_attributes` de la DB en modo alcance.
 */
const BLD_B_BUCKETS = [
  { key: '0', value: 0, label: '< 200 m²', shortLabel: '< 200', min: null, max: 200, maxExclusive: true },
  { key: '1', value: 1, label: '200–600 m²', shortLabel: '200–600', min: 200, max: 600 },
  { key: '2', value: 2, label: '600–1.500 m²', shortLabel: '600–1.5k', min: 600, max: 1500 },
  { key: '3', value: 3, label: '1.500–5.000 m²', shortLabel: '1.5k–5k', min: 1500, max: 5000 },
  { key: '4', value: 4, label: '≥ 5.000 m²', shortLabel: '≥ 5k', min: 5000, max: null },
];

/**
 * Valuación sobre el crudo `valuation` (USD), no `val_b`.
 * Las keys 0–3 se mantienen para el parse NL; el abierto ≥120k se partió.
 */
const VALUATION_BANDS = [
  { key: '0', value: 0, label: '< USD 10.000', shortLabel: '< 10k', min: null, max: 10000, maxExclusive: true },
  { key: '1', value: 1, label: 'USD 10.000–30.000', shortLabel: '10k–30k', min: 10000, max: 30000 },
  { key: '2', value: 2, label: 'USD 30.000–60.000', shortLabel: '30k–60k', min: 30000, max: 60000 },
  { key: '3', value: 3, label: 'USD 60.000–120.000', shortLabel: '60k–120k', min: 60000, max: 120000 },
  { key: '120k_500k', value: '120k_500k', label: 'USD 120.000–500.000', shortLabel: '120k–500k', min: 120000, max: 500000, maxExclusive: true },
  { key: '500k_1m', value: '500k_1m', label: 'USD 500.000–1.000.000', shortLabel: '500k–1M', min: 500000, max: 1000000, maxExclusive: true },
  { key: '1m_5m', value: '1m_5m', label: 'USD 1.000.000–5.000.000', shortLabel: '1M–5M', min: 1000000, max: 5000000, maxExclusive: true },
  { key: 'gte5m', value: 'gte5m', label: '≥ USD 5.000.000', shortLabel: '≥ 5M', min: 5000000, max: null },
];

/**
 * Superficie sobre el crudo (`urbansurface`, fallback `surface`).
 * Los 4 primeros chips quedan; ≥5.000 se parte en m² y hectáreas.
 */
const SURFACE_BANDS = [
  { key: 'lt200', value: 'lt200', label: '< 200 m²', shortLabel: '< 200', min: null, max: 200, maxExclusive: true },
  { key: '200-500', value: '200-500', label: '200–500 m²', shortLabel: '200–500', min: 200, max: 500 },
  { key: '500-1000', value: '500-1000', label: '500–1.000 m²', shortLabel: '500–1k', min: 500, max: 1000 },
  { key: '1000-5000', value: '1000-5000', label: '1.000–5.000 m²', shortLabel: '1k–5k', min: 1000, max: 5000 },
  { key: '5k_10k', value: '5k_10k', label: '5.000–10.000 m²', shortLabel: '5k–10k', min: 5000, max: 10000, maxExclusive: true },
  { key: '1_5ha', value: '1_5ha', label: '1–5 ha', shortLabel: '1–5 ha', min: 10000, max: 50000, maxExclusive: true },
  { key: '5_10ha', value: '5_10ha', label: '5–10 ha', shortLabel: '5–10 ha', min: 50000, max: 100000, maxExclusive: true },
  { key: '10_20ha', value: '10_20ha', label: '10–20 ha', shortLabel: '10–20 ha', min: 100000, max: 200000, maxExclusive: true },
  { key: '20_50ha', value: '20_50ha', label: '20–50 ha', shortLabel: '20–50 ha', min: 200000, max: 500000, maxExclusive: true },
  { key: '50_100ha', value: '50_100ha', label: '50–100 ha', shortLabel: '50–100 ha', min: 500000, max: 1000000, maxExclusive: true },
  { key: 'gte100ha', value: 'gte100ha', label: '≥ 100 ha', shortLabel: '≥ 100 ha', min: 1000000, max: null },
];

/**
 * El parse puede mandar el chip abierto viejo hasta que snapee a las keys nuevas.
 * @param {string} field
 * @param {unknown[]} values
 * @returns {unknown[]}
 */
export function expandLegacyParsedRangeBands(field, values) {
  if (!Array.isArray(values)) return values;
  if (field === 'surface') {
    const nextKeys = SURFACE_BANDS
      .filter((band) => band.min != null && band.min >= 5000)
      .map((band) => band.value);
    return [...new Set(values.flatMap((item) => (item === 'gte5000' ? nextKeys : [item])))];
  }
  if (field === 'valuation') {
    const nextKeys = VALUATION_BANDS
      .filter((band) => band.min != null && band.min >= 120000)
      .map((band) => band.value);
    return [...new Set(values.flatMap((item) => (
      item === 4 || item === '4' ? nextKeys : [item]
    )))];
  }
  return values;
}

const FOOTPRINT_BANDS = [
  { key: 'lt200', value: 'lt200', label: '< 200 m²', shortLabel: '< 200', min: null, max: 200, maxExclusive: true },
  { key: '200-600', value: '200-600', label: '200–600 m²', shortLabel: '200–600', min: 200, max: 600 },
  { key: '600-1500', value: '600-1500', label: '600–1.500 m²', shortLabel: '600–1.5k', min: 600, max: 1500 },
  { key: '1500-5000', value: '1500-5000', label: '1.500–5.000 m²', shortLabel: '1.5k–5k', min: 1500, max: 5000 },
  { key: 'gte5000', value: 'gte5000', label: '≥ 5.000 m²', shortLabel: '≥ 5k', min: 5000, max: null },
];

/** Headers compartidos de la matriz m² (Potencial / huellas). */
const M2_MATRIX_BAND_LABELS = ['< 200', '200–600', '600–1.5k', '1.5k–5k', '≥ 5k'];

const ALTURA_BANDS = [
  { key: 'lt9', value: 'lt9', label: '< 9 m', shortLabel: '< 9', min: null, max: 9, maxExclusive: true },
  { key: '9-15', value: '9-15', label: '9–15 m', shortLabel: '9–15', min: 9, max: 15 },
  { key: '15-30', value: '15-30', label: '15–30 m', shortLabel: '15–30', min: 15, max: 30 },
  { key: '30-60', value: '30-60', label: '30–60 m', shortLabel: '30–60', min: 30, max: 60 },
  { key: 'gte60', value: 'gte60', label: '≥ 60 m', shortLabel: '≥ 60', min: 60, max: null },
];

/**
 * Bandas de un porcentaje a partir de la leyenda de su capa de referencia, para
 * que el filtro use los mismos niveles que el mapa y el detalle del lote.
 * El techo de cada banda es el piso de la siguiente (exclusivo), así no quedan
 * huecos donde un lote no caiga en ninguna: la leyenda de inundación salta de 97
 * a 100 y la de pobreza corta con decimales.
 */
const bandsFromLegendLevels = (legend) => legend.map((item, index, all) => ({
  key: String(item.value),
  value: String(item.value),
  shortLabel: item.shortLabel || item.label,
  min: item.pctMin,
  max: all[index + 1]?.pctMin ?? null,
  maxExclusive: true,
}));

/** Cada bucket ya trae el rango en su etiqueta ("Bajo (1–4,99%)"). */
const CHRONIC_POVERTY_BANDS = bandsFromLegendLevels(POBREZA_CRONICA_ARGENTINA_BUCKETS)
  .map((band, index) => ({
    ...band,
    label: POBREZA_CRONICA_ARGENTINA_BUCKETS[index].label,
  }));

/**
 * Solo Leve / Moderada / Crítica: por debajo de 70 % no hay datos que filtrar.
 * Moderada llega a 99 para tapar el hueco 98–99 de la leyenda.
 * @see docs/maps/inundation-layer.md
 */
const FLOOD_RISK_BANDS = bandsFromLegendLevels(ZONAS_INUNDABLES_LEGEND).map((band) => ({
  ...band,
  label: band.max != null
    ? `${band.shortLabel} (${band.min}–${band.max - 1}%)`
    : `${band.shortLabel} (${band.min}%)`,
}));

/** Grupos del panel, por dominio conceptual. */
const FILTER_PANEL_GROUPS = [
  {
    id: 'basicos',
    label: 'Básicos',
    defaultOpen: true,
    accordion: false,
    filters: [
      {
        id: 'type',
        component: 'multiSelect',
        field: 'type',
        label: 'Tipo de propiedad',
        options: TYPE_OPTIONS,
      },
      {
        id: 'state',
        component: 'multiSelect',
        field: 'state',
        label: 'Estado',
        options: STATE_OPTIONS,
      },
      {
        id: 'neighborhood_type',
        component: 'multiSelect',
        field: 'neighborhood_type',
        label: 'Tipo de barrio',
        options: NEIGHBORHOOD_TYPE_OPTIONS,      },
    ],
  },
  {
    id: 'ubicacion',
    label: 'Ubicación y normativa',
    defaultOpen: false,
    accordion: true,
    filters: [
      {
        id: 'province_name',
        component: 'multiSelect',
        field: 'province_name',
        tileField: 'province_name',
        label: 'Provincia',
        options: PROVINCE_OPTIONS,
        searchable: true,
      },
      {
        id: 'neighborhood_name',
        component: 'multiSelect',
        field: 'neighborhood_name',
        tileField: 'neighborhood_name',
        label: 'Barrio',
        // Sin catálogo de barrios, las opciones son dinámicas: filas del alcance
        // si hay uno activo, y si no las parcelas cargadas en la vista. Las
        // provee MapPage junto con la leyenda que corresponde a cada origen.
        // @see docs/maps/parcel-filters-scope-backend-spec.md §9.3
        optionsFrom: 'map',
        searchable: true,
      },
      {
        id: 'street_name',
        component: 'textContains',
        field: 'street_name',
        tileField: 'street_name_norm',
        tileFieldFallback: 'street_name',
        label: 'Calle',
        placeholder: 'Ej: Chacabuco',
        minLength: 3,
        // El tile trae `street_name_norm` (minúsculas, sin acentos). La consulta
        // se normaliza igual. Si el tileset todavía no tiene el campo, se cae a
        // `street_name` (sin tolerancia a acentos).
        // @see docs/maps/parcel-filters-scope-backend-spec.md §9.2
        normalizeQuery: true,
      },
      {
        id: 'zoning_zone',
        component: 'multiSelect',
        field: 'zoning_zone',
        label: 'Zona normativa',
        options: ZONING_ZONE_OPTIONS,
      },
      {
        id: 'zoning_perfil',
        component: 'multiSelect',
        field: 'zoning_perfil',
        label: 'Perfil normativo',
        options: ZONING_PERFIL_OPTIONS,
      },
    ],
  },
  {
    id: 'dimensiones',
    label: 'Dimensiones y forma',
    defaultOpen: false,
    accordion: true,
    filters: [
      {
        id: 'surface',
        component: 'rangeBands',
        field: 'surface',
        tileField: 'urbansurface',
        tileFieldFallback: 'surface',
        dbField: 'urbansurface',
        dbFieldFallback: 'surface',
        label: 'Superficie',
        columns: 4,
        options: SURFACE_BANDS,
      },
      {
        id: 'lot_shape',
        component: 'multiSelect',
        field: 'lot_shape',
        label: 'Forma del lote',
        options: LOT_SHAPE_OPTIONS,      },
      {
        id: 'street_position',
        component: 'multiSelect',
        field: 'street_position',
        label: 'Posición en cuadra',
        options: STREET_POSITION_OPTIONS,      },
      {
        id: 'front_highway_type',
        component: 'multiSelect',
        field: 'front_highway_type',
        label: 'Tipo de calle',
        options: FRONT_HIGHWAY_OPTIONS,      },
    ],
  },
  {
    id: 'potencial',
    label: 'Potencial y valor',
    defaultOpen: false,
    accordion: true,
    filters: [
      {
        id: 'm2_matrix',
        component: 'rangeMatrix',
        unit: 'm²',
        bandLabels: M2_MATRIX_BAND_LABELS,
        rows: [
          {
            id: 'buildable',
            field: 'buildable',
            tileField: 'bld_b',
            dbField: 'max_buildable_m2',
            label: 'Potencial',
            matchMode: 'exact',
            options: BLD_B_BUCKETS,
          },
          {
            id: 'max_footprint_m2',
            field: 'max_footprint_m2',
            tileField: 'max_footprint_m2',
            label: 'H. máxima',
            options: FOOTPRINT_BANDS,
          },
          {
            id: 'built_footprint_m2',
            field: 'built_footprint_m2',
            tileField: 'built_footprint_m2',
            label: 'H. construida',
            options: FOOTPRINT_BANDS,
          },
        ],
      },
      {
        id: 'altura_max_m',
        component: 'rangeBands',
        field: 'altura_max_m',
        tileField: 'altura_max_m',
        label: 'Altura máxima',
        options: ALTURA_BANDS,
        scopeUnsupported: true,
      },
      {
        id: 'valuation',
        component: 'rangeBands',
        field: 'valuation',
        tileField: 'valuation',
        dbField: 'valuation',
        label: 'Valuación',
        shortLabel: 'Valuación',
        hint: 'Valor de mercado del lote en USD',
        columns: 4,
        options: VALUATION_BANDS,
      },
    ],
  },
  {
    id: 'construccion',
    label: 'Construcción',
    defaultOpen: true,
    accordion: false,
    filters: [
      {
        id: 'built',
        component: 'triState',
        field: 'built',
        label: 'Construcción',
        scopeUnsupported: true,
        options: [
          { key: 'all', value: null, label: 'Todos' },
          { key: 'yes', value: 1, label: 'Con construcción' },
          { key: 'no', value: 0, label: 'Sin construcción' },
        ],
      },
    ],
  },
  {
    id: 'contexto',
    label: 'Contexto y riesgo',
    defaultOpen: false,
    accordion: true,
    filters: [
      {
        id: 'flood_risk_pct',
        component: 'rangeBands',
        field: 'flood_risk_pct',
        tileField: 'flood_risk_pct',
        // Mismo nombre y mismos niveles que en el detalle del lote y la capa.
        label: 'Riesgo de inundación',
        shortLabel: 'Inundación',
        options: FLOOD_RISK_BANDS,
        columns: 3,
      },
      {
        id: 'chronic_poverty_pct',
        component: 'rangeBands',
        field: 'chronic_poverty_pct',
        tileField: 'chronic_poverty_pct',
        label: 'Pobreza crónica',
        shortLabel: 'Pobreza',
        options: CHRONIC_POVERTY_BANDS,
        columns: 3,
      },
      {
        id: 'water_network_deficit_pct',
        component: 'inlineBoolean',
        field: 'water_network_deficit_pct',
        tileField: 'water_network_deficit_pct',
        label: 'Sin agua de red',
        trueValue: 1,
        // Mismo umbral que Infraestructura en el detalle (Sí/No).
        compare: { op: '>=', value: 40 },
      },
      {
        id: 'sewer_deficit_pct',
        component: 'inlineBoolean',
        field: 'sewer_deficit_pct',
        tileField: 'sewer_deficit_pct',
        label: 'Sin cloaca',
        trueValue: 1,
        compare: { op: '>=', value: 40 },
      },
    ],
  },
];

/**
 * Filtros de la publicación (modalidad, moneda, precio, texto): datos privados
 * del dueño, no están en el tile ni en `filter_attributes`. Se evalúan por unidad
 * funcional en `Map/utils/sharedListingFilters.js`, que es quien entiende de UFs.
 *
 * `source: 'listing'` es la marca que usan los motores de parcelas para ignorarlos.
 * Grupo opt-in: vista compartida, o el mapa principal con alcance en registros / lista.
 *
 * @see docs/maps/parcel-filters-scope-backend-spec.md §5 (atributos privados)
 */
const LISTING_FILTER_GROUP = {
  id: 'publicacion',
  label: 'Publicación',
  defaultOpen: true,
  accordion: true,
  filters: [
    {
      id: 'listing_search',
      component: 'textContains',
      field: 'listing_search',
      source: 'listing',
      label: 'Buscar en la lista',
      shortLabel: 'Búsqueda',
      placeholder: 'Descripción, ubicación, precio…',
      minLength: 2,
    },
    {
      id: 'listing_operation',
      component: 'multiSelect',
      field: 'listing_operation',
      dbField: 'operation',
      source: 'listing',
      label: 'Modalidad',
      options: OPERATIONS.map(({ value, label }) => ({ key: value, value, label })),
    },
    {
      id: 'listing_currency',
      component: 'multiSelect',
      field: 'listing_currency',
      dbField: 'currency',
      source: 'listing',
      label: 'Moneda',
      options: CURRENCIES.map((value) => ({ key: value, value, label: value })),
    },
    {
      id: 'listing_price',
      component: 'rangeBands',
      field: 'listing_price',
      dbField: 'price',
      source: 'listing',
      label: 'Precio',
      // Un alquiler y una venta no comparten escala, y no hay cotización en el
      // front para mezclar monedas: las bandas se derivan de los precios reales
      // de la lista en la moneda elegida. Cada valor es "min:max" (piso abierto
      // = 0, max vacío = sin techo), así el match no depende del catálogo.
      optionsFrom: 'map',
      options: [],
    },
  ],
};

/** Bandas de conteo: “1”, “2”… y un “N o más” final. */
const countBands = (max, unitLabel) => [
  ...Array.from({ length: max - 1 }, (_, i) => {
    const n = i + 1;
    return {
      key: String(n),
      value: String(n),
      label: String(n),
      shortLabel: `${n} ${unitLabel}`,
      min: n,
      max: n,
    };
  }),
  {
    key: `gte${max}`,
    value: `gte${max}`,
    label: `${max}+`,
    shortLabel: `${max}+ ${unitLabel}`,
    min: max,
    max: null,
  },
];

const UNIT_SURFACE_BANDS = [
  { key: 'lt40', value: 'lt40', label: '< 40 m²', shortLabel: '< 40', min: null, max: 40, maxExclusive: true },
  { key: '40-70', value: '40-70', label: '40–70 m²', shortLabel: '40–70', min: 40, max: 70 },
  { key: '70-120', value: '70-120', label: '70–120 m²', shortLabel: '70–120', min: 70, max: 120 },
  { key: '120-200', value: '120-200', label: '120–200 m²', shortLabel: '120–200', min: 120, max: 200 },
  { key: 'gte200', value: 'gte200', label: '≥ 200 m²', shortLabel: '≥ 200', min: 200, max: null },
];

const BUILDING_TYPE_OPTIONS = [
  { key: 'departamento', value: 'departamento', label: 'Departamento' },
  { key: 'casa', value: 'casa', label: 'Casa' },
  { key: 'ph', value: 'ph', label: 'PH' },
  { key: 'comercio', value: 'comercio', label: 'Comercio' },
];

/**
 * Atributos que el dueño cargó en cada unidad funcional, del bloque
 * `private_attributes` del listado. Mismo `source: 'listing'` que Publicación:
 * se evalúan por unidad y no existen en el tile.
 */
const LISTING_UNIT_FILTER_GROUP = {
  id: 'unidad',
  label: 'Características de la unidad',
  defaultOpen: false,
  accordion: true,
  filters: [
    {
      id: 'building_type',
      component: 'multiSelect',
      field: 'building_type',
      source: 'listing',
      label: 'Tipo de unidad',
      options: BUILDING_TYPE_OPTIONS,
    },
    {
      id: 'total_surface',
      component: 'rangeBands',
      field: 'total_surface',
      source: 'listing',
      label: 'Superficie de la unidad',
      options: UNIT_SURFACE_BANDS,
    },
    {
      id: 'rooms',
      component: 'rangeBands',
      field: 'rooms',
      source: 'listing',
      label: 'Ambientes',
      options: countBands(5, 'amb.'),
    },
    {
      id: 'bedrooms',
      component: 'rangeBands',
      field: 'bedrooms',
      source: 'listing',
      label: 'Dormitorios',
      options: countBands(4, 'dorm.'),
    },
    {
      id: 'full_bathrooms',
      component: 'rangeBands',
      field: 'full_bathrooms',
      source: 'listing',
      label: 'Baños',
      options: countBands(3, 'baños'),
    },
    {
      id: 'parking_lots',
      component: 'inlineBoolean',
      field: 'parking_lots',
      source: 'listing',
      label: 'Con cochera',
      trueValue: 1,
      compare: { op: '>=', value: 1 },
    },
    {
      id: 'has_water',
      component: 'inlineBoolean',
      field: 'has_water',
      source: 'listing',
      label: 'Con agua',
      trueValue: 1,
    },
    {
      id: 'has_gas',
      component: 'inlineBoolean',
      field: 'has_gas',
      source: 'listing',
      label: 'Con gas',
      trueValue: 1,
    },
    {
      id: 'has_electricity',
      component: 'inlineBoolean',
      field: 'has_electricity',
      source: 'listing',
      label: 'Con electricidad',
      trueValue: 1,
    },
    {
      id: 'has_sewage',
      component: 'inlineBoolean',
      field: 'has_sewage',
      source: 'listing',
      label: 'Con cloaca',
      trueValue: 1,
    },
  ],
};

/**
 * UF catastrales oficiales (Rentas / padrón). No están en el tile ni en
 * `filter_attributes`: el mapa pinta el lote padre si **alguna** UF cumple.
 * Motor: `POST /filterofficialunits` (bbox + predicados → lot_ids).
 *
 * `source: 'official_unit'` hace que el match de parcela (tile / DB) las ignore.
 *
 * @see docs/maps/official-unit-filters.md
 */

/**
 * Bandas de valuación fiscal (ARS). Tramos temporales: hay que actualizarlos
 * y validarlos con valores de mercado.
 */
const OFFICIAL_FISCAL_BANDS = [
  {
    key: 'lt50k',
    value: 'lt50k',
    label: '< $50 mil',
    shortLabel: '< 50 mil',
    min: null,
    max: 50000,
    maxExclusive: true,
  },
  {
    key: '50to150',
    value: '50to150',
    label: '$50–150 mil',
    shortLabel: '50–150 mil',
    min: 50000,
    max: 150000,
    maxExclusive: true,
  },
  {
    key: '150to400',
    value: '150to400',
    label: '$150–400 mil',
    shortLabel: '150–400 mil',
    min: 150000,
    max: 400000,
    maxExclusive: true,
  },
  {
    key: 'gte400',
    value: 'gte400',
    label: '$400 mil o más',
    shortLabel: '400 mil+',
    min: 400000,
    max: null,
  },
];

const OFFICIAL_UNIT_FILTER_GROUP = {
  id: 'uf_catastral',
  label: 'Unidades funcionales catastrales',
  defaultOpen: true,
  accordion: true,
  filters: [
    {
      id: 'official_unit_search',
      component: 'textContains',
      field: 'official_unit_search',
      source: 'official_unit',
      label: 'Nomenclatura catastral o cuenta',
      shortLabel: 'Nomenclatura / cuenta',
      placeholder: 'N° catastral o número de cuenta',
      minLength: 2,
    },
    {
      id: 'official_built_surface',
      component: 'rangeBands',
      field: 'official_built_surface',
      dbField: 'built_surface_m2',
      source: 'official_unit',
      label: 'Superficie construida',
      shortLabel: 'Sup. UF',
      options: UNIT_SURFACE_BANDS,
    },
    {
      id: 'official_fiscal_total',
      component: 'rangeBands',
      field: 'official_fiscal_total',
      dbField: 'fiscal_total_ars',
      source: 'official_unit',
      label: 'Valuación fiscal',
      shortLabel: 'Fiscal',
      options: OFFICIAL_FISCAL_BANDS,
    },
  ],
};

/** Expande matrices a filas `rangeBands` para match / chips / conteo. */
function expandFilterDefinition(def) {
  if (def.component !== 'rangeMatrix') return [def];
  return (def.rows || []).map((row) => ({
    ...row,
    component: 'rangeBands',
  }));
}

/**
 * Todos los grupos declarados, sin las exclusiones que hace el panel según el
 * contexto: es la fuente de las definiciones (match, estado, chips, conteo).
 */
const ALL_FILTER_GROUPS = [
  LISTING_FILTER_GROUP,
  LISTING_UNIT_FILTER_GROUP,
  OFFICIAL_UNIT_FILTER_GROUP,
  ...FILTER_PANEL_GROUPS,
];

/** Tabs del panel: parcela vs unidades (oficiales + publicación). */
export const FILTER_PANEL_TAB = {
  lot: 'lot',
  units: 'units',
};

const isParcelFilterDef = (def) => def.source !== 'listing' && def.source !== 'official_unit';

/**
 * Grupos a dibujar en el panel.
 * @param {{
 *   includeListing?: boolean,
 *   hideScopeUnsupported?: boolean,
 *   tab?: 'lot'|'units',
 *   includeOfficialUnits?: boolean,
 * }} [options]
 *   `tab` separa Lote / Unidades. `includeListing` agrega Publicación y
 *   Características (alcance registros / lista). `includeOfficialUnits` el
 *   grupo catastral (hoy Córdoba). `hideScopeUnsupported` esconde `built` y
 *   `altura_max_m` con alcance activo.
 */
export function getFilterPanelGroups({
  includeListing = false,
  hideScopeUnsupported = false,
  tab = FILTER_PANEL_TAB.lot,
  includeOfficialUnits = false,
} = {}) {
  let groups;
  if (tab === FILTER_PANEL_TAB.units) {
    groups = [
      ...(includeOfficialUnits ? [OFFICIAL_UNIT_FILTER_GROUP] : []),
      ...(includeListing ? [LISTING_FILTER_GROUP, LISTING_UNIT_FILTER_GROUP] : []),
    ];
  } else {
    groups = FILTER_PANEL_GROUPS;
  }
  if (!hideScopeUnsupported) return groups;

  return groups
    .map((group) => ({
      ...group,
      filters: group.filters.filter((def) => !def.scopeUnsupported),
    }))
    .filter((group) => group.filters.length > 0);
}

/**
 * Lista plana de definiciones (filas de matriz incluidas). Deliberadamente no
 * pasa por `getFilterPanelGroups`: lo que el panel oculta en una vista sigue
 * siendo parte del contrato en la otra.
 */
export function getActiveFilterDefinitions() {
  return ALL_FILTER_GROUPS.flatMap((g) => g.filters.flatMap(expandFilterDefinition));
}

/** Definiciones que evalúan atributos de parcela (tile o `filter_attributes`). */
export function getParcelFilterDefinitions() {
  return getActiveFilterDefinitions().filter(isParcelFilterDef);
}

/**
 * Grupo del panel que contiene el campo (Lote, Publicación o unidad).
 * Location → `ubicacion`.
 * @param {string|null|undefined} field
 * @returns {string}
 */
export function getFilterGroupIdForField(field) {
  if (!field) return 'ubicacion';
  const group = ALL_FILTER_GROUPS.find((item) => (
    item.filters.flatMap(expandFilterDefinition).some((def) => def.field === field)
  ));
  return group?.id || 'basicos';
}

/** Definiciones que evalúan datos de la publicación (por unidad funcional). */
export function getListingFilterDefinitions() {
  return getActiveFilterDefinitions().filter((def) => def.source === 'listing');
}

/** Definiciones de UF catastrales oficiales (no van al tile). */
export function getOfficialUnitFilterDefinitions() {
  return getActiveFilterDefinitions().filter((def) => def.source === 'official_unit');
}

/** Cantidad de filtros catastrales activos. */
export function countActiveOfficialUnitFilters(filters) {
  return getOfficialUnitFilterDefinitions().reduce(
    (count, def) => (isFilterValueActive(filters[def.field], def) ? count + 1 : count),
    0,
  );
}

/** Estado vacío según contrato. */
export function createEmptyParcelFilters() {
  const filters = {};
  getActiveFilterDefinitions().forEach((def) => {
    filters[def.field] = null;
  });
  return filters;
}

/** ¿El valor del filtro está activo? */
export function isFilterValueActive(value, def) {
  if (value == null) return false;
  if (def.component === 'multiSelect') return Array.isArray(value) && value.length > 0;
  if (def.component === 'rangeBands') return Array.isArray(value) && value.length > 0;
  if (def.component === 'triState') return value === 0 || value === 1;
  if (def.component === 'inlineBoolean') return value === def.trueValue;
  if (def.component === 'textContains') {
    return typeof value === 'string' && value.trim().length >= (def.minLength || 1);
  }
  return false;
}

/** Cuenta filtros activos. */
export function countActiveParcelFilters(filters) {
  return getActiveFilterDefinitions().reduce(
    (count, def) => (isFilterValueActive(filters[def.field], def) ? count + 1 : count),
    0,
  );
}

/**
 * Chips removibles para la barra superior.
 * @param {Record<string, unknown>} filters
 * @param {Record<string, { options?: Array<object> }>} [dynamicOptionsByField] opciones
 *   provistas por la vista para los filtros con `optionsFrom`, para etiquetar el chip.
 */
export function getAppliedFilterChips(filters, dynamicOptionsByField = null) {
  const chips = [];
  getActiveFilterDefinitions().forEach((def) => {
    const value = filters[def.field];
    if (!isFilterValueActive(value, def)) return;

    const fieldLabel = def.shortLabel || def.label;
    const push = (chip) => chips.push({ ...chip, field: def.field });

    if (def.component === 'multiSelect' || def.component === 'rangeBands') {
      const options = def.optionsFrom
        ? (dynamicOptionsByField?.[def.field]?.options || [])
        : (def.options || []);

      value.forEach((v) => {
        // Sin opción conocida (barrio sin catálogo) el valor ya es la etiqueta.
        const opt = options.find((o) => o.value === v);
        push({
          id: `${def.field}-${v}`,
          label: `${fieldLabel}: ${opt?.shortLabel || opt?.label || v}`,
          removeValue: v,
        });
      });
      return;
    }

    if (def.component === 'triState') {
      const opt = def.options.find((o) => o.value === value);
      push({ id: def.field, label: `${fieldLabel}: ${opt?.label || value}`, removeValue: null });
      return;
    }

    if (def.component === 'inlineBoolean') {
      push({ id: def.field, label: fieldLabel, removeValue: null });
      return;
    }

    if (def.component === 'textContains') {
      push({ id: def.field, label: `${fieldLabel}: ${value.trim()}`, removeValue: null });
    }
  });
  return chips;
}

/**
 * Campos que `filter_attributes` no puede evaluar todavía (`built`,
 * `altura_max_m`): con un alcance activo el panel los oculta y su valor se limpia.
 * @see docs/maps/parcel-filters-scope-backend-spec.md §8.1
 */
export function getScopeUnsupportedFields() {
  return getActiveFilterDefinitions()
    .filter((def) => def.scopeUnsupported)
    .map((def) => def.field);
}

const clearFieldsFromDefinitions = (filters, definitions) => {
  const fields = definitions.map((def) => def.field);
  if (fields.every((field) => filters?.[field] == null)) return filters;
  const next = { ...filters };
  fields.forEach((field) => { next[field] = null; });
  return next;
};

/** Descarta los filtros de parcela (tab Lote). No toca listing ni UF oficiales. */
export function clearParcelFilters(filters) {
  return clearFieldsFromDefinitions(filters, getParcelFilterDefinitions());
}

/** Descarta los filtros de publicación al salir del alcance. */
export function clearListingFilters(filters) {
  return clearFieldsFromDefinitions(filters, getListingFilterDefinitions());
}

/** Descarta UF catastrales al salir de una provincia con padrón. */
export function clearOfficialUnitFilters(filters) {
  return clearFieldsFromDefinitions(filters, getOfficialUnitFilterDefinitions());
}
export function clearScopeUnsupportedFilters(filters) {
  const fields = getScopeUnsupportedFields();
  if (fields.every((field) => filters?.[field] == null)) return filters;

  const next = { ...filters };
  fields.forEach((field) => { next[field] = null; });
  return next;
}

/** Conteo activo por grupo (para badge en acordeón). */
export function countActiveFiltersInGroup(group, filters) {
  return group.filters
    .flatMap(expandFilterDefinition)
    .reduce(
      (count, def) => (isFilterValueActive(filters[def.field], def) ? count + 1 : count),
      0,
    );
}

/** Suma de activos en varios grupos (badges de tab Lote / Unidades). */
export function countActiveFiltersInGroups(groups, filters) {
  return (groups || []).reduce(
    (count, group) => count + countActiveFiltersInGroup(group, filters),
    0,
  );
}
